import { createSlice, freeze, PayloadAction } from '@reduxjs/toolkit';
import { IDashboardInstanceSlice, IDraftDashboardCustomization } from './types';
import { assertCondition, assertNotNil } from '../../../../utils/types';
import {
  FilterType,
  IDashboardFolderFieldsFragment,
  IDashboardInstanceFieldsFragment,
  IFilterFieldsFragment,
  ISubFolderFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { MaybeNil } from '../../../../utils/interfaces';
import { IDateInput } from '../../oldBuilder/date/types';
import { normalizeFilter } from '../../../../components/FilterWidget/filterUtils';
import { getRowsWithNormalizedColSpans } from '../../../../pages/DashboardRoute/utils/grid/gridUpdaters';
import { isNil } from 'lodash';

const defaultDraftDashboardCustomization = freeze<IDraftDashboardCustomization>(
  {
    isActive: false,
    filter: null,
    dateRange: null,
    datePicker: null,
  },
  true,
);

const initialState = freeze<IDashboardInstanceSlice>(
  {
    dashboard: null,
    draftCustomization: defaultDraftDashboardCustomization,
    lastVisitedDashboardPathname: null,
    isLoaded: false,
    templateChangeCounter: 0,
    publishedFolder: null,
  },
  true,
);

export default createSlice({
  name: 'dashboardInstance',
  initialState,
  reducers: {
    dashboardInstancePopulate: (
      draft,
      action: PayloadAction<{
        dashboard: IDashboardInstanceFieldsFragment;
        lastVisitedDashboardPathname?: string;
      }>,
    ) => {
      const { dashboard, lastVisitedDashboardPathname } = action.payload;

      const result = {
        ...initialState,
        publishedFolder: draft.publishedFolder,
        dashboard: {
          ...dashboard,
          rows: getRowsWithNormalizedColSpans(dashboard.rows),
        },
        isLoaded: true,
        lastVisitedDashboardPathname:
          lastVisitedDashboardPathname || draft.lastVisitedDashboardPathname,
      } satisfies IDashboardInstanceSlice;

      const { filter } = dashboard;
      if (filter) {
        result.draftCustomization = {
          isActive: true,
          dateRange: filter.dateRange ?? null,
          filter: filter.filter ?? null,
          datePicker: filter.datePicker ?? null,
        };
      }

      return result;
    },
    dashboardInstanceUpdatePath: (
      draft,
      action: PayloadAction<{
        newPath: ISubFolderFieldsFragment[];
      }>,
    ) => {
      const { newPath } = action.payload;
      assertNotNil(draft.dashboard);
      draft.dashboard.path = newPath;
    },
    dashboardCustomizationActivate: (draft) => {
      draft.draftCustomization.isActive = !draft.draftCustomization.isActive;
    },
    dashboardCustomizationReset: (draft) => {
      draft.draftCustomization = defaultDraftDashboardCustomization;
    },
    dashboardCustomizationSetFilter: (
      draft,
      action: PayloadAction<{
        newFilter: IFilterFieldsFragment | null;
      }>,
    ) => {
      const { newFilter } = action.payload;

      draft.draftCustomization.filter = normalizeFilter(newFilter);
    },
    dashboardCustomizationExpandNamedFilter: (draft) => {
      const currentFilter = draft.draftCustomization.filter;

      assertCondition(currentFilter?.__typename === 'NamedFilter');

      draft.draftCustomization.filter = normalizeFilter({
        __typename: 'BasicFilter',
        type: FilterType.Basic,
        conditions: currentFilter.conditions,
        logicOps: currentFilter.logicOps,
      });
    },
    dashboardCustomizationSetDateRange: (
      draft,
      action: PayloadAction<{
        newDateInput: MaybeNil<IDateInput>;
      }>,
    ) => {
      const { newDateInput } = action.payload;

      draft.draftCustomization.dateRange = newDateInput
        ? {
            __typename: 'DateRange',
            start: newDateInput.startDate,
            end: newDateInput.endDate,
          }
        : null;
      draft.draftCustomization.datePicker = newDateInput?.datePicker ?? null;
    },
    dashboardInstanceUpdateReportInfo: (
      draft,
      action: PayloadAction<{
        analysisId: string;
        newName: string;
        newDescription: string;
      }>,
    ) => {
      const { analysisId, newName, newDescription } = action.payload;
      if (!isNil(draft.dashboard)) {
        draft.dashboard?.rows.forEach((row) => {
          row.columns.forEach((column) => {
            if (
              column.__typename === 'DashboardAnalysisColumn' &&
              column.analysis.id === analysisId
            ) {
              column.analysis.display = newName;
              column.analysis.description = newDescription;
            }
          });
        });
      }
    },
    dashboardInstanceToggleFavorite: (draft) => {
      assertNotNil(draft.dashboard);
      draft.dashboard.isFavorite = !draft.dashboard.isFavorite;
      draft.templateChangeCounter++;
    },
    dashboardInstanceResetLastVisited: (draft) => {
      // When we delete dashboard instance we should reset lastVisitedDashboardPathname
      // to avoid redirect to already deleted dashboard
      draft.lastVisitedDashboardPathname = null;
    },
    dashboardInstanceReset: (draft) => {
      assertNotNil(draft.dashboard);
      return {
        ...initialState,
        lastVisitedDashboardPathname: draft.lastVisitedDashboardPathname,
      };
    },
    reloadDashboardTemplates: (draft) => {
      draft.templateChangeCounter++;
    },

    populatePublishedFolder: (
      draft,
      action: PayloadAction<{
        publishedFolder: IDashboardFolderFieldsFragment;
      }>,
    ) => {
      draft.publishedFolder = action.payload.publishedFolder;
    },
    deleteSubFolder: (
      draft,
      action: PayloadAction<{
        subFolderId: string;
      }>,
    ) => {
      const { subFolderId } = action.payload;
      assertNotNil(draft.publishedFolder);
      draft.publishedFolder.subFolders =
        draft.publishedFolder.subFolders.filter(
          (subFolder) => subFolder.id !== subFolderId,
        );
    },
    renameSubFolder: (
      draft,
      action: PayloadAction<{
        subFolderId: string;
        newName: string;
      }>,
    ) => {
      const { subFolderId, newName } = action.payload;
      assertNotNil(draft.publishedFolder);
      const subFolder = draft.publishedFolder.subFolders.find(
        (folder) => folder.id === subFolderId,
      );
      if (subFolder) {
        subFolder.name = newName;
      }
    },
  },
});
